/**
 * Langer Druck mit dem Stift öffnet die Werkzeugpalette.
 *
 * Warum das hier steht: Das Drücken am Apple Pencil Pro und das Doppeltippen
 * am Pencil 2 melden sich ausschliesslich nativ über UIPencilInteraction.
 * Safari kennt beide Gesten nicht, im Browser kommt davon nichts an. Statt
 * die Palette dort nur über den Knopf erreichbar zu lassen, gibt es diese
 * Ersatzgeste: Stiftspitze aufsetzen, kurz liegen lassen, Palette geht an
 * genau dieser Stelle auf.
 *
 * Sie greift nur, wenn gerade kein Werkzeug aktiv ist. Beim Zeichnen wäre
 * ein langer Druck ein Punkt und keine Geste, und über Textfeldern gehört
 * der Stift dem Scribble von iPadOS.
 */
import { getToolState } from './tools.ts';

/** Wie lange die Spitze liegen muss. */
const HOLD_MS = 550;
/** Wie weit sie sich dabei bewegen darf. */
const SLOP = 8;

export function watchPencilLongPress(onOpen: (x: number, y: number) => void): () => void {
  let timer = 0;
  let startX = 0;
  let startY = 0;
  let fired = false;

  function cancel(): void {
    clearTimeout(timer);
    timer = 0;
  }

  function onDown(ev: PointerEvent): void {
    if (ev.pointerType !== 'pen') return;
    if (getToolState().active) return;

    // Über Eingabefeldern schreibt der Stift, dort nicht stören.
    const target = ev.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, .palette')) return;

    startX = ev.clientX;
    startY = ev.clientY;
    fired = false;
    cancel();
    timer = window.setTimeout(() => {
      fired = true;
      onOpen(startX, startY);
    }, HOLD_MS);
  }

  function onMove(ev: PointerEvent): void {
    if (!timer || ev.pointerType !== 'pen') return;
    if (Math.abs(ev.clientX - startX) > SLOP || Math.abs(ev.clientY - startY) > SLOP) cancel();
  }

  function onUp(): void {
    cancel();
  }

  /** Nach der Geste nicht auch noch das Tippen darunter auslösen. */
  function onClick(ev: MouseEvent): void {
    if (!fired) return;
    fired = false;
    ev.preventDefault();
    ev.stopPropagation();
  }

  document.addEventListener('pointerdown', onDown, true);
  document.addEventListener('pointermove', onMove, true);
  document.addEventListener('pointerup', onUp, true);
  document.addEventListener('pointercancel', onUp, true);
  document.addEventListener('click', onClick, true);

  return () => {
    cancel();
    document.removeEventListener('pointerdown', onDown, true);
    document.removeEventListener('pointermove', onMove, true);
    document.removeEventListener('pointerup', onUp, true);
    document.removeEventListener('pointercancel', onUp, true);
    document.removeEventListener('click', onClick, true);
  };
}
